import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  MAX_SERVICE_COVER_BYTES,
  MAX_SERVICE_DOCUMENT_BYTES,
  SERVICE_COVER_TYPES,
  SERVICE_DOCUMENT_TYPES,
  SERVICE_MEDIA_BUCKET,
  type CatalogService,
  type ContactMethod,
  type ServiceCategory,
  type ServiceCtaType,
  type ServiceDocument,
  type ServiceFaqItem,
  type ServiceRequestListItem,
  type ServiceRequestStatus,
  type ServiceRequestTopic,
  type UserPractice,
} from "@/lib/services/types";
import { resolveServiceCoverUrl } from "@/lib/services/covers";
import { asArray, asObject, createId, safeStorageExtension } from "@/lib/utils";
import type { AdminServiceDocument } from "@/lib/admin/platform-types";

type CategoryRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
};

type DocumentRow = {
  id: string;
  title: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  storage_path: string;
  sort_order: number;
};

type ServiceRow = {
  id: string;
  slug: string;
  name: string;
  summary: string;
  description: string;
  icon_name: string | null;
  icon_color: string | null;
  cover_path: string | null;
  cta_type: ServiceCtaType;
  cta_label: string;
  pricing_model: string;
  price_amount: number | string | null;
  price_unit: string | null;
  currency: string;
  benefits: unknown;
  target_audience: unknown;
  contact_details: unknown;
  is_featured: boolean;
  sort_order: number;
  provider_name: string | null;
  terms_text: string | null;
  terms_version: string | null;
  category: CategoryRow | CategoryRow[] | null;
  service_partners: {
    is_primary: boolean;
    partner: { id: string; name: string } | { id: string; name: string }[] | null;
  }[];
  service_documents: DocumentRow[] | null;
};

const SERVICE_SELECT = `
  id,
  slug,
  name,
  summary,
  description,
  icon_name,
  icon_color,
  cover_path,
  cta_type,
  cta_label,
  pricing_model,
  price_amount,
  price_unit,
  currency,
  benefits,
  target_audience,
  contact_details,
  is_featured,
  sort_order,
  provider_name,
  terms_text,
  terms_version,
  category:service_categories (
    id,
    name,
    slug,
    description,
    sort_order
  ),
  service_partners (
    is_primary,
    partner:partners (
      id,
      name
    )
  ),
  service_documents (
    id,
    title,
    file_name,
    mime_type,
    size_bytes,
    storage_path,
    sort_order
  )
`;

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === "string");
}

function asRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
}

function parseFaq(value: unknown): ServiceFaqItem[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const record = asRecord(item);
    const question = record.question;
    const answer = record.answer;

    if (typeof question !== "string" || typeof answer !== "string") {
      return [];
    }

    return [{ question, answer }];
  });
}

function parseDocuments(
  rows: DocumentRow[] | null,
  urls: Map<string, string>,
): ServiceDocument[] {
  return [...(rows ?? [])]
    .sort((left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0))
    .map((row) => ({
      id: row.id,
      title: row.title,
      fileName: row.file_name,
      sizeBytes: Number(row.size_bytes) || 0,
      url: urls.get(row.storage_path) ?? null,
    }));
}

function mapCategory(row: CategoryRow): ServiceCategory {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    description: row.description,
    sortOrder: row.sort_order,
  };
}

function mapService(
  row: ServiceRow,
  urls: Map<string, string>,
): CatalogService | null {
  const category = asObject(row.category);

  if (!category) {
    return null;
  }

  const audience = asRecord(row.target_audience);
  const contactDetails = asRecord(row.contact_details);
  const primaryPartner = asArray(row.service_partners).find((item) => item.is_primary)
    ?? asArray(row.service_partners)[0];

  const priceAmount =
    row.price_amount === null || row.price_amount === undefined
      ? null
      : Number(row.price_amount);

  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    summary: row.summary,
    description: row.description,
    iconName: row.icon_name,
    iconColor: row.icon_color,
    coverUrl: resolveServiceCoverUrl(
      row.cover_path ? urls.get(row.cover_path) ?? null : null,
      category.slug,
    ),
    ctaType: row.cta_type,
    ctaLabel: row.cta_label,
    pricingModel: row.pricing_model,
    priceAmount: Number.isFinite(priceAmount) ? priceAmount : null,
    priceUnit: row.price_unit,
    currency: row.currency,
    benefits: asStringArray(row.benefits),
    audienceSummary: typeof audience.summary === "string" ? audience.summary : null,
    problemStatement: typeof audience.problem === "string" ? audience.problem : null,
    faq: parseFaq(contactDetails.faq),
    documents: parseDocuments(row.service_documents, urls),
    category: mapCategory(category),
    partnerName:
      (typeof row.provider_name === "string" && row.provider_name.trim()
        ? row.provider_name
        : null) ?? asObject(primaryPartner?.partner)?.name ?? null,
    isFeatured: Boolean(row.is_featured),
    termsText: row.terms_text,
    termsVersion: row.terms_version,
  };
}

export async function signServiceMedia(paths: (string | null | undefined)[]) {
  const uniquePaths = [...new Set(paths.filter((path): path is string => Boolean(path)))];
  const urls = new Map<string, string>();
  if (uniquePaths.length === 0) {
    return urls;
  }

  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.storage
    .from(SERVICE_MEDIA_BUCKET)
    .createSignedUrls(uniquePaths, 60 * 60);

  if (error) {
    return urls;
  }

  for (const item of data ?? []) {
    if (item.path && item.signedUrl) {
      urls.set(item.path, item.signedUrl);
    }
  }

  return urls;
}

export async function uploadServiceCover(input: { userId: string; file: File }) {
  if (!SERVICE_COVER_TYPES.includes(input.file.type as (typeof SERVICE_COVER_TYPES)[number])) {
    throw new Error("Csak JPG, PNG vagy WebP borítókép tölthető fel.");
  }
  if (input.file.size > MAX_SERVICE_COVER_BYTES) {
    throw new Error("A borítókép legfeljebb 8 MB lehet.");
  }

  const supabase = getSupabaseBrowserClient();
  const extension = safeStorageExtension(input.file.name, "jpg");
  const path = `${input.userId}/cover-${createId()}.${extension}`;
  const { error } = await supabase.storage
    .from(SERVICE_MEDIA_BUCKET)
    .upload(path, input.file, {
      cacheControl: "3600",
      contentType: input.file.type,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return path;
}

export async function uploadServiceDocument(input: {
  userId: string;
  file: File;
}): Promise<AdminServiceDocument> {
  if (
    !SERVICE_DOCUMENT_TYPES.includes(
      input.file.type as (typeof SERVICE_DOCUMENT_TYPES)[number],
    )
  ) {
    throw new Error("Csak PDF, Word, Excel, PowerPoint vagy szöveges fájl tölthető fel.");
  }
  if (input.file.size > MAX_SERVICE_DOCUMENT_BYTES) {
    throw new Error("A dokumentum legfeljebb 15 MB lehet.");
  }

  const supabase = getSupabaseBrowserClient();
  const extension = safeStorageExtension(input.file.name);
  const path = `${input.userId}/doc-${createId()}.${extension}`;
  const { error } = await supabase.storage
    .from(SERVICE_MEDIA_BUCKET)
    .upload(path, input.file, {
      cacheControl: "3600",
      contentType: input.file.type,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return {
    title: input.file.name.replace(/\.[^.]+$/, ""),
    fileName: input.file.name,
    mimeType: input.file.type,
    sizeBytes: input.file.size,
    storagePath: path,
  };
}

async function hydrateCatalog(rows: ServiceRow[]) {
  const urls = await signServiceMedia([
    ...rows.map((row) => row.cover_path),
    ...rows.flatMap((row) =>
      (row.service_documents ?? []).map((document) => document.storage_path),
    ),
  ]);

  return rows
    .map((row) => mapService(row, urls))
    .filter((service): service is CatalogService => Boolean(service));
}

export async function getServiceCategories(): Promise<ServiceCategory[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("service_categories")
    .select("id, name, slug, description, sort_order")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => mapCategory(row as CategoryRow));
}

export async function getCatalogServices(): Promise<CatalogService[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("services")
    .select(SERVICE_SELECT)
    .eq("status", "active")
    .order("is_featured", { ascending: false })
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }

  return hydrateCatalog((data ?? []) as unknown as ServiceRow[]);
}

export async function getCatalogServiceBySlug(slug: string): Promise<CatalogService | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("services")
    .select(SERVICE_SELECT)
    .eq("slug", slug)
    .eq("status", "active")
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return null;
  }

  const [service] = await hydrateCatalog([data as unknown as ServiceRow]);
  return service ?? null;
}

export async function getMyPractices(): Promise<UserPractice[]> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();

  if (authError) {
    throw authError;
  }

  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from("practice_members")
    .select(
      `
      is_primary,
      status,
      practice:practices (
        id,
        name,
        city,
        is_active
      )
    `,
    )
    .eq("user_id", user.id)
    .eq("status", "active");

  if (error) {
    throw error;
  }

  return (data ?? []).flatMap((row) => {
    const practice = asObject(
      row.practice as
        | { id: string; name: string; city: string; is_active: boolean }
        | { id: string; name: string; city: string; is_active: boolean }[]
        | null,
    );

    if (!practice || practice.is_active === false) {
      return [];
    }

    return [
      {
        id: practice.id,
        name: practice.name,
        city: practice.city,
        isPrimary: Boolean(row.is_primary),
      },
    ];
  });
}

export async function getMyServiceRequests(): Promise<ServiceRequestListItem[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("service_requests")
    .select(
      `
      id,
      status,
      created_at,
      updated_at,
      service:services (
        name,
        slug
      ),
      practice:practices (
        name,
        city
      )
    `,
    )
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).flatMap((row) => {
    const service = asObject(
      row.service as { name: string; slug: string } | { name: string; slug: string }[] | null,
    );
    const practice = asObject(
      row.practice as
        | { name: string; city: string | null }
        | { name: string; city: string | null }[]
        | null,
    );

    if (!service || !practice) {
      return [];
    }

    return [
      {
        id: row.id,
        status: row.status as ServiceRequestStatus,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        serviceName: service.name,
        serviceSlug: service.slug,
        practiceName: practice.name,
        practiceCity: practice.city,
      },
    ];
  });
}

export async function createServiceRequest(input: {
  userId: string;
  serviceId: string;
  practiceId: string;
  topic: ServiceRequestTopic;
  description: string;
  contactMethod: ContactMethod;
  contactPhone?: string;
  preferredContactAt?: string | null;
  idempotencyKey: string;
  termsAccepted?: boolean;
  termsVersion?: string | null;
  termsOpenedAt?: string | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("service_requests")
    .insert({
      user_id: input.userId,
      practice_id: input.practiceId,
      service_id: input.serviceId,
      source: "service_page",
      status: "new",
      idempotency_key: input.idempotencyKey,
      contact_phone: input.contactPhone || null,
      preferred_contact_at: input.preferredContactAt || null,
      form_data: {
        topic: input.topic,
        description: input.description,
        contact_method: input.contactMethod,
        terms_accepted: Boolean(input.termsAccepted),
        terms_version: input.termsVersion ?? null,
        terms_opened_at: input.termsOpenedAt ?? null,
      },
    })
    .select("id")
    .single();

  if (error) {
    if (error.code === "23505") {
      const { data: existing, error: existingError } = await supabase
        .from("service_requests")
        .select("id")
        .eq("user_id", input.userId)
        .eq("idempotency_key", input.idempotencyKey)
        .single();

      if (!existingError) {
        return existing.id as string;
      }
    }

    throw error;
  }

  return data.id as string;
}
